import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, FolderKanban, MessageSquare, Tags, Ban, Trash2, Star, ShieldCheck, Plus, FileText, Store,
} from "lucide-react";
import { MarketAdminTab } from "@/components/market/MarketAdminTab";
import * as Icons from "lucide-react";
import api, { mediaUrl, timeAgo, formatApiError } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <p className="eyebrow text-primary flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Área administrativa</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Painel administrativo</h1>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard icon={Users} label="Usuários" value={stats?.users} />
        <StatCard icon={FolderKanban} label="Projetos" value={stats?.projects} />
        <StatCard icon={FileText} label="Publicados" value={stats?.published} />
        <StatCard icon={FileText} label="Rascunhos" value={stats?.drafts} />
        <StatCard icon={MessageSquare} label="Comentários" value={stats?.comments} />
        <StatCard icon={Ban} label="Bloqueados" value={stats?.blocked_users} />
      </div>

      <Tabs defaultValue="users" className="mt-8">
        <TabsList className="rounded-full flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full gap-1.5"><Users className="h-4 w-4" /> Usuários</TabsTrigger>
          <TabsTrigger value="projects" data-testid="admin-tab-projects" className="rounded-full gap-1.5"><FolderKanban className="h-4 w-4" /> Projetos</TabsTrigger>
          <TabsTrigger value="comments" data-testid="admin-tab-comments" className="rounded-full gap-1.5"><MessageSquare className="h-4 w-4" /> Comentários</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories" className="rounded-full gap-1.5"><Tags className="h-4 w-4" /> Categorias</TabsTrigger>
          <TabsTrigger value="market" data-testid="admin-tab-market" className="rounded-full gap-1.5"><Store className="h-4 w-4" /> Mercado</TabsTrigger>
        </TabsList>
        <TabsContent value="market" className="mt-6"><MarketAdminTab /></TabsContent>

        <TabsContent value="users" className="mt-6"><UsersTab onChange={() => api.get("/admin/stats").then((r) => setStats(r.data))} /></TabsContent>
        <TabsContent value="projects" className="mt-6"><ProjectsTab onChange={() => api.get("/admin/stats").then((r) => setStats(r.data))} /></TabsContent>
        <TabsContent value="comments" className="mt-6"><CommentsTab onChange={() => api.get("/admin/stats").then((r) => setStats(r.data))} /></TabsContent>
        <TabsContent value="categories" className="mt-6"><CategoriesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <Icon className="h-5 w-5 text-primary" />
      <p className="mt-2 text-2xl font-extrabold">{value ?? "—"}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function Confirm({ trigger, title, desc, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{desc}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={onConfirm} className="bg-destructive hover:bg-destructive/90">Confirmar</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function UsersTab({ onChange }) {
  const [users, setUsers] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const block = async (u) => { try { await api.post(`/admin/users/${u.id}/block`); toast.success("Status atualizado"); load(); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (u) => { try { await api.delete(`/admin/users/${u.id}`); toast.success("Usuário excluído"); load(); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (users === null) return <Loader />;
  return (
    <div className="space-y-2">
      {users.map((u) => (
        <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
          <img src={mediaUrl(u.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{u.name} {u.role === "admin" && <span className="ml-1 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">ADMIN</span>} {u.blocked && <span className="ml-1 rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-bold text-destructive">BLOQUEADO</span>}</p>
            <p className="truncate text-xs text-muted-foreground">{u.email} · {u.projects_count} projeto(s)</p>
          </div>
          {u.role !== "admin" && (
            <>
              <Button data-testid={`block-user-${u.id}`} variant="outline" size="sm" onClick={() => block(u)} className="rounded-full gap-1"><Ban className="h-3.5 w-3.5" /> {u.blocked ? "Desbloquear" : "Bloquear"}</Button>
              <Confirm trigger={<Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button>}
                title="Excluir usuário?" desc="O usuário e todos os seus projetos serão removidos." onConfirm={() => del(u)} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ProjectsTab({ onChange }) {
  const [projects, setProjects] = useState(null);
  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const feature = async (p) => { try { await api.post(`/admin/projects/${p.id}/feature`); toast.success("Destaque atualizado"); load(); } catch { toast.error("Erro"); } };
  const del = async (p) => { try { await api.delete(`/projects/${p.id}`); toast.success("Projeto removido"); load(); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (projects === null) return <Loader />;
  return (
    <div className="space-y-2">
      {projects.map((p) => (
        <div key={p.id} data-testid={`admin-project-${p.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
          <img src={mediaUrl(p.cover)} alt="" className="h-12 w-16 rounded-lg object-cover bg-secondary" />
          <div className="flex-1 min-w-0">
            <Link to={`/projeto/${p.slug}`} className="font-semibold text-sm hover:text-primary truncate block">{p.title}</Link>
            <p className="text-xs text-muted-foreground">{p.author?.name} · {p.status === "draft" ? "Rascunho" : "Publicado"} · ❤️ {p.likes_count}</p>
          </div>
          <Button data-testid={`feature-project-${p.id}`} variant={p.is_featured ? "default" : "outline"} size="sm" onClick={() => feature(p)} className="rounded-full gap-1"><Star className={`h-3.5 w-3.5 ${p.is_featured ? "fill-current" : ""}`} /> Destaque</Button>
          <Confirm trigger={<Button data-testid={`delete-project-${p.id}`} variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button>}
            title="Remover projeto?" desc="O projeto será removido permanentemente." onConfirm={() => del(p)} />
        </div>
      ))}
    </div>
  );
}

function CommentsTab({ onChange }) {
  const [comments, setComments] = useState(null);
  const load = () => api.get("/admin/comments").then((r) => setComments(r.data)).catch(() => setComments([]));
  useEffect(() => { load(); }, []);
  const del = async (c) => { try { await api.delete(`/comments/${c.id}`); toast.success("Comentário removido"); load(); onChange(); } catch { toast.error("Erro"); } };

  if (comments === null) return <Loader />;
  if (!comments.length) return <p className="text-sm text-muted-foreground">Nenhum comentário.</p>;
  return (
    <div className="space-y-2">
      {comments.map((c) => (
        <div key={c.id} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3">
          <img src={mediaUrl(c.author?.avatar)} alt="" className="h-9 w-9 rounded-full object-cover" />
          <div className="flex-1">
            <p className="text-sm">{c.text}</p>
            <p className="text-xs text-muted-foreground">{c.author?.name} em "{c.project_title}" · {timeAgo(c.created_at)}</p>
          </div>
          <Confirm trigger={<Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button>}
            title="Excluir comentário?" desc="Esta ação é permanente." onConfirm={() => del(c)} />
        </div>
      ))}
    </div>
  );
}

function CategoriesTab() {
  const [cats, setCats] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get("/categories").then((r) => setCats(r.data)).catch(() => setCats([]));
  useEffect(() => { load(); }, []);

  const add = async () => { if (!name.trim()) return; try { await api.post("/admin/categories", { name: name.trim() }); setName(""); toast.success("Categoria adicionada"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (slug) => { try { await api.delete(`/admin/categories/${slug}`); toast.success("Removida"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (cats === null) return <Loader />;
  return (
    <div className="max-w-xl">
      <div className="flex gap-2">
        <Input data-testid="admin-category-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nova categoria" className="rounded-xl" />
        <Button data-testid="admin-add-category-btn" onClick={add} className="rounded-full gap-1.5"><Plus className="h-4 w-4" /> Adicionar</Button>
      </div>
      <div className="mt-4 space-y-2">
        {cats.map((c) => {
          const Icon = Icons[c.icon] || Icons.Boxes;
          return (
            <div key={c.slug} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <Icon className="h-5 w-5 text-primary" />
              <span className="flex-1 font-medium text-sm">{c.name}</span>
              <span className="text-xs text-muted-foreground">{c.count}</span>
              <Confirm trigger={<Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button>}
                title="Remover categoria?" desc="Apenas categorias personalizadas podem ser removidas." onConfirm={() => del(c.slug)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
